// Petits helpers visuels du système Tickets : la carte de couleurs des catégories vit ici pour que
// la page, les modales et le dashboard parlent la même langue chromatique. Uniquement des classes
// Tailwind littérales (pas de token shadcn, pas de couleur construite dynamiquement : le purger de
// Tailwind ne garde que ce qu'il voit).

export interface CategoryTone {
	/** Classes du chip (bordure + fond + texte), tons pastel cohérents avec le reste de l'admin. */
	chip: string;
	/** Classe du point de couleur associé (pour les listes denses). */
	dot: string;
	/** Rail gauche des cartes de la liste (couleur de bordure côté gauche, posée sur border-l-4). */
	rail: string;
	/** Barre d'accent horizontale du panneau de détail (fond plein, ton médian). */
	bar: string;
}

const TONES: Record<string, CategoryTone> = {
	sky: { chip: "border-sky-200 bg-sky-50 text-sky-700", dot: "bg-sky-500", rail: "border-l-sky-400", bar: "bg-sky-400" },
	violet: { chip: "border-violet-200 bg-violet-50 text-violet-700", dot: "bg-violet-500", rail: "border-l-violet-400", bar: "bg-violet-400" },
	teal: { chip: "border-teal-200 bg-teal-50 text-teal-700", dot: "bg-teal-500", rail: "border-l-teal-400", bar: "bg-teal-400" },
	amber: { chip: "border-amber-200 bg-amber-50 text-amber-700", dot: "bg-amber-500", rail: "border-l-amber-400", bar: "bg-amber-400" },
	rose: { chip: "border-rose-200 bg-rose-50 text-rose-700", dot: "bg-rose-500", rail: "border-l-rose-400", bar: "bg-rose-400" },
	indigo: { chip: "border-indigo-200 bg-indigo-50 text-indigo-700", dot: "bg-indigo-500", rail: "border-l-indigo-400", bar: "bg-indigo-400" },
	slate: { chip: "border-slate-200 bg-slate-100 text-slate-600", dot: "bg-slate-400", rail: "border-l-slate-300", bar: "bg-slate-300" }
};

/** Les six catégories servies par l'API (TicketCategories côté C#) ont une couleur attitrée ;
 *  « bug-plateforme » est volontairement en rose (c'est l'anomalie), « autre » reste neutre. */
const CATEGORY_TONE_KEYS: Record<string, string> = {
	contenu: "sky",
	traduction: "violet",
	texte: "teal",
	media: "amber",
	"bug-plateforme": "rose",
	autre: "slate"
};

/** Réserve de tons pour une éventuelle catégorie inconnue : hash stable → toujours la même couleur. */
const HASH_POOL = ["sky", "violet", "teal", "amber", "indigo"];

export const categoryTone = (key: string): CategoryTone => {
	const named = CATEGORY_TONE_KEYS[key];
	if (named) return TONES[named];
	let hash = 0;
	for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) | 0;
	return TONES[HASH_POOL[Math.abs(hash) % HASH_POOL.length]];
};

/**
 * Ordre du panneau Tickets du dashboard : les tickets ASSIGNÉS À MOI d'abord (ordre serveur,
 * les plus récents en tête), puis les autres tickets récents : dédupliqués (un ticket déjà
 * présent dans « à moi » n'apparaît pas une seconde fois). Pure et générique pour être testable.
 */
export const orderDashboardTickets = <T extends { id: string }>(mine: T[], recent: T[]): Array<T & { mine: boolean }> => {
	const mineIds = new Set(mine.map((t) => t.id));
	return [
		...mine.map((t) => ({ ...t, mine: true })),
		...recent.filter((t) => !mineIds.has(t.id)).map((t) => ({ ...t, mine: false }))
	];
};
